<script setup lang="ts">
import type { Name as ThemeName } from '#theme'
import type { UIDialogStepEntry, UIDialogSequenceContext } from '../../types'
import { computed, inject, onBeforeUnmount, provide, ref, shallowRef, watch, useI18n, useNotifications, usePreferredReducedMotion, useTheme, useTimeoutFn, UIDIALOG_SEQUENCE_KEY, DIALOG_SEQUENCE_LOADER_MAX_MS, DIALOG_SEQUENCE_TOAST_MS, DIALOG_SEQUENCE_TOAST_TYPE } from '#imports'
import { onKeyStroke, promiseTimeout, useElementSize, useMediaQuery } from '@vueuse/core'

const NUDGE_KEYFRAMES: Keyframe[] = [
    { transform: 'translateY(0) scale(1)' },
    { transform: 'translateY(6px) scale(0.995)', offset: 0.35 },
    { transform: 'translateY(0) scale(1)' }
]
// Stroke inset, so the rim sits on the sheet's edge instead of half outside it.
const RIM_INSET = 1.75

// Lets the closing step leave before the toast lands, so the two don't move at once.
const TOAST_DELAY_MS = 320

const STEP_IN_KEYFRAMES: Keyframe[] = [
    { opacity: 0, transform: 'translateX(24px)' },
    { opacity: 1, transform: 'translateX(0)' }
]

const { t } = useI18n()
// A nested UIApp (some pages wrap themselves for theming) leaves its steps and toasts to the outer host.
const parentSequence = inject(UIDIALOG_SEQUENCE_KEY, null)
const reducedMotion = usePreferredReducedMotion()

const steps = shallowRef<UIDialogStepEntry[]>([])
const completedCount = ref(0)
const isSheetOpened = ref(false)
const activeId = ref<string>()
// The step an interrupting step took the sheet from; it gets the sheet back when that one closes.
const coveredId = ref<string>()
const holds = ref(0)
const isLoaderExpired = ref(false)

const sheetEl = ref<HTMLElement>()
const lockNoteEl = ref<HTMLElement>()
const rimLiveEl = ref<SVGPathElement>()
const rimAnimation = shallowRef<Animation>()
const topEl = ref<HTMLElement>()
const headerEl = ref<HTMLElement>()
const bodyEl = ref<HTMLElement>()
const footerEl = ref<HTMLElement>()
const overlayEl = ref<HTMLElement>()

// Stable sort, so equal priorities keep registration order.
const pendingSteps = computed(() => [...steps.value].sort((a, b) => b.priority - a.priority))
// The step that closed the sheet, frozen as it was, so the sheet animates out with it instead of emptying first.
const leavingStep = shallowRef<UIDialogStepEntry>()
const activeStep = computed(() => steps.value.find(step => step.id === activeId.value) ?? leavingStep.value)
// An interrupting step opens on top of the step showing: without a theme of its own the sheet keeps that step's, so it
// doesn't switch colours (e.g. terms over the light auth sheet on a dark brand) as it widens.
const { theme } = useTheme()
const sheetTheme = computed(() => {
    const step = activeStep.value
    const covered = step?.interrupt ? steps.value.find(({ id }) => id === coveredId.value) : undefined

    // The site theme is an installed one; Theme.name is typed over every core theme.
    return step?.theme ?? covered?.theme ?? theme.value.name as ThemeName
})
const totalCount = computed(() => completedCount.value + steps.value.filter(step => !step.interrupt).length)
// A step that unmounts (its page changed, e.g. login → forgot password) usually has a successor loading;
// the sheet stays up for it, so the page swaps instead of the sheet closing and opening again.
const HANDOFF_MS = 1000
const isHandingOff = ref(false)
const { start: startHandoff, stop: stopHandoff } = useTimeoutFn(() => {
    isHandingOff.value = false
    isSheetOpened.value = false
}, HANDOFF_MS, { immediate: false })

const isOpen = computed(() => !!steps.value.length || isHandingOff.value)
const isLoading = computed(() => isOpen.value && !activeStep.value)

// Pick the next step only once every held source has answered, and keep it until it closes:
// a step that arrives later queues behind the visible one instead of replacing it.
watch([steps, holds, isLoaderExpired], () => {
    if (activeStep.value || (holds.value && !isLoaderExpired.value)) return

    activeId.value = pendingSteps.value[0]?.id
}, { immediate: true })

const { start: startLoaderTimer, stop: stopLoaderTimer } = useTimeoutFn(() => {
    isLoaderExpired.value = true
}, DIALOG_SEQUENCE_LOADER_MAX_MS, { immediate: false })

watch(isLoading, (loading) => {
    if (loading) return startLoaderTimer()

    stopLoaderTimer()
})

function hold(work: PromiseLike<unknown>) {
    if (import.meta.server) return

    holds.value++

    // allSettled: a failing source is the query's own error to report; here it only stops holding.
    // At most the loader's own limit: a slow background source must not keep a step the player
    // opened later (delete a passkey, cancel a bonus) behind the loader.
    Promise.race([Promise.allSettled([work]), promiseTimeout(DIALOG_SEQUENCE_LOADER_MAX_MS)]).finally(() => {
        holds.value--
    })
}

const { allNotifications, remove: removeNotification } = useNotifications()
const shownToastId = ref<string>()
const queuedToast = computed(() => allNotifications.value.find(notification => notification.type === DIALOG_SEQUENCE_TOAST_TYPE))
// Read from the queue, so a toast cleared on a page change leaves the screen with it.
const toast = computed(() => {
    const notification = allNotifications.value.find(({ id }) => id === shownToastId.value)

    if (!notification) return

    return { id: notification.id, message: notification.value?.message ?? '', danger: notification.value?.type === 'danger' }
})

const { start: showToastSoon } = useTimeoutFn(() => {
    if (!queuedToast.value) return

    shownToastId.value = queuedToast.value.id
    hideToastLater()
}, TOAST_DELAY_MS, { immediate: false })

const { start: hideToastLater } = useTimeoutFn(() => {
    if (shownToastId.value) removeNotification(shownToastId.value)

    shownToastId.value = undefined
}, DIALOG_SEQUENCE_TOAST_MS, { immediate: false })

if (import.meta.client) {
    // Immediate: a toast queued before the host mounted (added during SSR) still shows.
    watch(() => queuedToast.value?.id, (id) => {
        if (id && !parentSequence) showToastSoon()
    }, { immediate: true })
}

function register(entry: UIDialogStepEntry) {
    if (steps.value.some(step => step.name === entry.name)) return false

    stopHandoff()
    isHandingOff.value = false

    if (leavingStep.value) onClosed()

    steps.value = [...steps.value, entry]

    if (entry.interrupt && activeStep.value && !activeStep.value.interrupt) {
        coveredId.value = activeStep.value.id
        activeId.value = entry.id
    }

    return true
}

function unregister(id: string, { handoff = false }: { handoff?: boolean } = {}) {
    const entry = steps.value.find(step => step.id === id)

    if (!entry) return

    const isClosingActive = activeStep.value?.id === id && !handoff

    // Only a step the player saw and closed counts as done; a pending one, or one whose page changed, just drops out.
    if (isClosingActive && !entry.interrupt) {
        completedCount.value++
    }

    steps.value = steps.value.filter(step => step.id !== id)

    if (coveredId.value === id) coveredId.value = undefined

    // An interrupting step hands the sheet back to the step it covered.
    if (entry.interrupt && activeId.value === id && coveredId.value) {
        activeId.value = coveredId.value
        coveredId.value = undefined
    }

    // A step that registers again later must queue like any other, not find itself still picked.
    if (activeId.value === id) activeId.value = undefined

    const isLast = !steps.value.length

    // The last step to close stays up while the sheet animates out and hears `closed` after; any other step
    // that closes or drops out is gone at once. A hand-off isn't closed: its page is swapping, not leaving.
    if (isLast && isClosingActive) {
        // Spread: the entry's getters read live props, which the closing step may already be resetting.
        leavingStep.value = { ...entry }
        isSheetOpened.value = false
        isLoaderExpired.value = false

        return
    }

    if (!handoff) entry.closed()

    if (!isLast) return

    isLoaderExpired.value = false
    completedCount.value = 0

    // An interrupting step has no page successor: when it unmounts as the last step (Back while it covers a step
    // whose page is gone too) nothing is coming, so the sheet leaves instead of waiting on its loader.
    if (!handoff || entry.interrupt) {
        isSheetOpened.value = false

        return
    }

    isHandingOff.value = true
    startHandoff()
}

function onClosed() {
    // A step that reopened the sheet mid-exit already took over; a late closed event must not reset it.
    const step = leavingStep.value

    if (!step) return

    leavingStep.value = undefined
    completedCount.value = 0
    step.closed()
}

function isCovered(id: string) {
    return coveredId.value === id
}

function isActive(id: string) {
    return !!activeStep.value && activeStep.value.id === id
}

function animate(el: Element | null | undefined, keyframes: Keyframe[], options: KeyframeAnimationOptions) {
    if (reducedMotion.value === 'reduce' || typeof el?.animate !== 'function') return

    el.animate(keyframes, options)
}

function onDismiss() {
    const step = activeStep.value

    if (!step || leavingStep.value || step.busy) return

    // Emitted regardless of closeOnBackdropClick so steps can intercept, like UIDialog's backdrop-click.
    step.backdropClick()

    // The handler may have closed it already; the next step must not be dismissed by the same click.
    if (activeStep.value !== step || leavingStep.value) return

    // A required step is answered, never dismissed, whatever its closeOnBackdropClick says.
    if (step.closeOnBackdropClick && !step.required) {
        return step.close()
    }

    animate(bodyEl.value?.closest('.dialog'), NUDGE_KEYFRAMES, { duration: 320, easing: 'ease' })

    if (step.required) flashLockNote()
}

function flashLockNote() {
    const el = lockNoteEl.value

    if (!el) return

    const idle = getComputedStyle(el).color
    const alert = getComputedStyle(el).getPropertyValue('--primary').trim() || idle

    animate(el, [
        { color: idle },
        { color: alert, offset: 0.18 },
        { color: idle, offset: 0.36 },
        { color: alert, offset: 0.54 },
        { color: idle }
    ], { duration: 900, easing: 'ease' })
}

const { width: sheetWidth, height: sheetHeight } = useElementSize(sheetEl)
// A step's top slot (e.g. the DK self-assessment bar) sits above the heading; the close icon moves down with it.
const { height: topSlotHeight } = useElementSize(topEl, undefined, { box: 'border-box' })

const showRim = computed(() => !!activeStep.value?.highlight && reducedMotion.value !== 'reduce')

// UIDialog's breakpoint ($screen-sm-md, SCSS-only): from there up the sheet is a centred dialog, not a bottom sheet.
const isCenteredDialog = useMediaQuery('(min-width: 600px)')

// Up the left edge, across the rounded top, down the right edge: the outline of a bottom sheet, which sits on the
// screen's edge. The centred dialog has a bottom edge of its own, so its outline closes. Its corners can be square (LuxCasino).
const rimPath = computed(() => {
    const w = Math.round(sheetWidth.value)
    const h = Math.round(sheetHeight.value)
    const style = sheetEl.value ? getComputedStyle(sheetEl.value.closest('.dialog') ?? sheetEl.value) : undefined
    const radius = Number.parseFloat(style?.borderTopLeftRadius ?? '') || 0
    const r = radius + RIM_INSET
    const edge = w - RIM_INSET
    const top = `L${RIM_INSET},${r} Q${RIM_INSET},${RIM_INSET} ${r},${RIM_INSET} L${w - r},${RIM_INSET} Q${edge},${RIM_INSET} ${edge},${r}`

    if (!isCenteredDialog.value) return `M${RIM_INSET},${h} ${top} L${edge},${h}`

    const rb = (Number.parseFloat(style?.borderBottomLeftRadius ?? '') || 0) + RIM_INSET
    const bottom = h - RIM_INSET

    return `M${RIM_INSET},${h - rb} ${top} L${edge},${h - rb} Q${edge},${bottom} ${w - rb},${bottom} L${rb},${bottom} Q${RIM_INSET},${bottom} ${RIM_INSET},${h - rb} Z`
})

watch([rimLiveEl, rimPath], ([el]) => {
    rimAnimation.value?.cancel()
    rimAnimation.value = undefined

    if (!el || typeof el.animate !== 'function' || typeof el.getTotalLength !== 'function') return

    const length = el.getTotalLength()
    const dash = Math.max(90, length * 0.16)

    el.style.strokeDasharray = `${dash} ${length - dash}`
    rimAnimation.value = el.animate([
        { strokeDashoffset: dash, opacity: 0, offset: 0 },
        { strokeDashoffset: dash * 0.55, opacity: 1, offset: 0.07 },
        { strokeDashoffset: -(length - dash), opacity: 1, offset: 0.66 },
        { strokeDashoffset: -(length - dash), opacity: 0, offset: 0.76 },
        { strokeDashoffset: -(length - dash), opacity: 0, offset: 1 }
    ], { duration: 2100, easing: 'linear', iterations: Infinity })
}, { flush: 'post' })

onBeforeUnmount(() => rimAnimation.value?.cancel())

onKeyStroke('Escape', onDismiss)

// A step is "opened" once it is visible: after the sheet's entrance animation, or right away when it
// takes over an already open sheet.
watch([() => activeStep.value?.id, isSheetOpened], ([id, opened]) => {
    if (!id || !opened) return

    activeStep.value?.opened()
})

watch(() => activeStep.value?.id, (id, previousId) => {
    if (!id || !previousId) return

    const scroller = bodyEl.value?.parentElement

    // Each step starts at its top, also the one an interrupting step hands back (e.g. signup after its terms).
    if (scroller) scroller.scrollTop = 0

    animate(scroller, STEP_IN_KEYFRAMES, { duration: 380, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' })
}, { flush: 'post' })

if (!parentSequence) provide<UIDialogSequenceContext>(UIDIALOG_SEQUENCE_KEY, {
    register,
    unregister,
    isActive,
    isCovered,
    hasStep: (name: string) => steps.value.some(step => step.name === name),
    hold,
    theme: sheetTheme,
    topEl,
    headerEl,
    bodyEl,
    footerEl,
    overlayEl
})

const kicker = computed(() => {
    if (activeStep.value?.required) return t('core.components.UIDialogSequence.required')

    return activeStep.value?.kicker
})

// A step with its own header slot, or no header text at all, starts its body at the top of the sheet.
const hasHeading = computed(() => !!activeStep.value && !activeStep.value.hasHeaderSlot && !!(activeStep.value.header || kicker.value))

const showCloseIcon = computed(() => !!activeStep.value && !activeStep.value.required && activeStep.value.closeIcon)
// No progress row or heading above the body: its own title would start under the close icon.
const bodyUnderClose = computed(() => showCloseIcon.value && !hasHeading.value && (totalCount.value <= 1 || !!activeStep.value?.interrupt))

function onCloseIconClick() {
    const step = activeStep.value

    if (!step) return

    step.closeIconClick()

    // As with onDismiss: the handler may have closed it, and the next step must not be closed by the same click.
    if (activeStep.value !== step || leavingStep.value) return

    if (step.closeOnCloseIconClick) step.close()
}

const announcement = computed(() => {
    const header = activeStep.value?.header ?? ''

    if (totalCount.value <= 1) return header

    return t('core.components.UIDialogSequence.stepOf', {
        n: completedCount.value + 1,
        total: totalCount.value,
        header
    })
})
</script>

<template>
    <template v-if="!parentSequence">
        <!-- Before the slot on purpose: the server writes teleports in render order, and the client hydrates this
             host ahead of lazily loaded dialogs in the slot (AuthContainer), so theirs have to come after. -->
        <UIDialog
            :model-value="isOpen"
            :theme="sheetTheme"
            variant="promotion"
            transition-name="bounce-down"
            :show-header="false"
            :scrollable="false"
            :close-on-backdrop-click="false"
            :backdrop-props="activeStep?.backdropClass ? { class: activeStep.backdropClass } : undefined"
            class="dialog-sequence"
            :class="{ 'dialog-sequence--wide': activeStep?.wide }"
            data-test="dialog-sequence"
            @backdrop-click="onDismiss"
            @opened="isSheetOpened = true"
            @closed="onClosed"
        >
            <div ref="sheetEl" class="dialog-sequence__sheet" :style="{ minHeight: activeStep?.minHeight, maxHeight: activeStep?.maxHeight }">
                <svg
                    v-if="showRim && sheetWidth && sheetHeight"
                    class="dialog-sequence__rim"
                    :viewBox="`0 0 ${Math.round(sheetWidth)} ${Math.round(sheetHeight)}`"
                    aria-hidden="true"
                >
                    <path class="dialog-sequence__rim-base" :d="rimPath" />
                    <path ref="rimLiveEl" class="dialog-sequence__rim-live" :d="rimPath" />
                </svg>

                <UIButton
                    v-if="showCloseIcon"
                    color="link"
                    icon
                    size="sm"
                    class="dialog-sequence__close"
                    :style="{ '--dialog-sequence-top-slot-height': `${topSlotHeight}px` }"
                    :aria-label="t('core.close')"
                    :disabled="activeStep?.busy"
                    @click="onCloseIconClick"
                >
                    <UIIcon name="close" size="16" />
                </UIButton>

                <div class="dialog-sequence__top">
                    <!-- A real box, not display: contents, so the close icon can sit below what a step puts here. -->
                    <div ref="topEl" class="dialog-sequence__top-slot" />

                    <div
                        v-if="activeStep && !activeStep.interrupt && totalCount > 1"
                        class="dialog-sequence__progress"
                        :class="{ 'dialog-sequence__progress--has-close': showCloseIcon }"
                        role="progressbar"
                        :aria-valuenow="completedCount + 1"
                        aria-valuemin="1"
                        :aria-valuemax="totalCount"
                    >
                        <div
                            v-for="i in totalCount"
                            :key="i"
                            class="dialog-sequence__progress-seg"
                            :class="{
                                'dialog-sequence__progress-seg--done': i <= completedCount,
                                'dialog-sequence__progress-seg--active': i === completedCount + 1
                            }"
                            data-test="dialog-sequence-progress-seg"
                        >
                            <div class="dialog-sequence__progress-fill" />
                        </div>
                    </div>

                    <div ref="headerEl" class="dialog-sequence__slot" />

                    <div
                        v-if="hasHeading"
                        class="dialog-sequence__heading"
                        :class="{ 'dialog-sequence__heading--has-close': showCloseIcon, 'dialog-sequence__heading--start': activeStep?.headingStart }"
                    >
                        <div v-if="kicker" class="dialog-sequence__kicker" v-text="kicker" />

                        <h2 v-if="activeStep?.header" class="dialog-sequence__title" v-text="activeStep.header" />

                        <div
                            v-if="activeStep?.required && !activeStep.hasFooterSlot"
                            ref="lockNoteEl"
                            class="dialog-sequence__lock-note"
                            data-test="dialog-sequence-lock-note"
                        >
                            <UIIcon name="lock" size="13" />
                            <span>
                                <strong v-text="t('core.components.UIDialogSequence.required')" />
                                {{ t('core.components.UIDialogSequence.requiredNote') }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- inert while leaving: its buttons stay on screen during the exit, but the step was already answered. -->
                <div
                    class="dialog-sequence__body"
                    :class="{ 'dialog-sequence__body--flush': activeStep?.flush, 'dialog-sequence__body--after-heading': hasHeading, 'dialog-sequence__body--under-close': bodyUnderClose }"
                    :inert="!!leavingStep"
                >
                    <div v-if="isLoading" class="dialog-sequence__loader" data-test="dialog-sequence-loader">
                        <UILoader text="" />
                    </div>

                    <div ref="bodyEl" class="dialog-sequence__slot" />
                </div>

                <div v-if="activeStep?.hasFooterSlot" class="dialog-sequence__footer" data-test="dialog-sequence-footer">
                    <!-- Next to the button it explains, when the step has one. -->
                    <div
                        v-if="activeStep.required"
                        ref="lockNoteEl"
                        class="dialog-sequence__lock-note dialog-sequence__lock-note--footer"
                        data-test="dialog-sequence-lock-note"
                    >
                        <UIIcon name="lock" size="13" />
                        <span>
                            <strong v-text="t('core.components.UIDialogSequence.required')" />
                            {{ t('core.components.UIDialogSequence.requiredNote') }}
                        </span>
                    </div>

                    <div ref="footerEl" class="dialog-sequence__slot" />
                </div>

                <div ref="overlayEl" class="dialog-sequence__overlay" />
            </div>

            <span
                class="dialog-sequence__aria-live"
                aria-live="polite"
                aria-atomic="true"
                v-text="announcement"
            />
        </UIDialog>

        <!-- Client-only: a toast only ever follows a player action, and an empty SSR'd teleport would shift the
             hydration of dialogs teleported after it from lazily loaded components. -->
        <ClientOnly>
            <Teleport to="#teleports">
                <Transition name="dialog-sequence-toast">
                    <div
                        v-if="toast"
                        :key="toast.id"
                        class="dialog-sequence-toast"
                        :class="{ 'dialog-sequence-toast--danger': toast.danger }"
                        :role="toast.danger ? 'alert' : 'status'"
                        data-test="dialog-sequence-toast"
                    >
                        <UIIcon :name="toast.danger ? 'warning' : 'check'" size="17" class="dialog-sequence-toast__icon" />
                        <!-- eslint-disable-next-line vue/no-v-html -- copy from our i18n and the promotion service, which carries a My bonuses link -->
                        <span v-html="toast.message" />
                    </div>
                </Transition>
            </Teleport>
        </ClientOnly>
    </template>

    <slot />
</template>

<style lang="scss" scoped>
.dialog-sequence-toast {
    position: fixed;
    top: 48px;
    right: 16px;
    left: 16px;
    z-index: z(toast);
    // #teleports passes clicks through; the toast must catch them, or a tap starts the game underneath.
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 9px;
    max-width: 420px;
    margin: 0 auto;
    padding: 13px 15px;
    background: $dialog-sheet-raised-bg;
    color: var(--on-surface, $dialog-font-color);
    border-radius: var(--border-radius-lg, $border-radius-lg);
    box-shadow:
        0 8px 24px -8px rgba(20, 26, 40, 0.3),
        0 0 0 1px $dialog-sheet-hairline;
    font-size: $font-size-sm;
    font-weight: $text-semi-bold;

    &__icon {
        flex-shrink: 0;
        color: var(--success, $brand-success);
    }

    &--danger &__icon {
        color: var(--danger, $brand-danger);
    }

    &-enter-active,
    &-leave-active {
        transition:
            opacity 0.22s ease,
            transform 0.22s ease;
    }

    &-enter-from,
    &-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}

.dialog-sequence {
    // Each step fits its content, so as much of the page as possible stays in view; long steps set a
    // minHeight. Top and footer stay put, only the step content scrolls.
    &__sheet {
        position: relative;
        display: grid;
        // Centred for most steps by the rule below.
        text-align: left;
        // Top and footer at their size; minmax(0, …) lets the body shrink under max-height and scroll.
        grid-template-rows: auto minmax(0, 1fr) auto;
        min-height: 200px;
        max-height: min(75dvh, 640px);

        // Link buttons ("Not now", "Maybe later") are bold in every step.
        :deep(.btn--link) {
            font-weight: $text-bold;
        }
    }

    // A step that asks for input reads from the left edge, and so does a wide step (long reading, e.g. terms);
    // every other step is centred. Fields hidden with v-show (a step covered by an interrupting one, a page of a
    // step's own flow) don't count: v-show writes display: none inline. Without :has() support the rule drops and
    // the sheet stays left-aligned.
    &__sheet:where(:not(.dialog-sequence--wide *)):where(
            :not(:has(:is(input:not([type='hidden']), select, textarea):not([style*='display: none'] *)))
        ) {
        --dialog-sequence-align: center;

        text-align: center;

        // Lists stay left-aligned, so each item starts under the one before.
        :deep(:where(ul, ol)) {
            text-align: left;
        }

        // Padded on both sides, so the title is centred on the sheet and not beside the close icon.
        .dialog-sequence__heading--has-close:not(.dialog-sequence__heading--start) {
            padding-left: 44px;
        }

        .dialog-sequence__lock-note {
            justify-content: center;
        }
    }

    &__close {
        position: absolute;
        top: calc(15px + var(--dialog-sequence-top-slot-height, 0px));
        right: 15px;
        // Above the overlay: a step that keeps its close icon while covered leaves the player a way out.
        z-index: 4;
        color: var(--on-surface, $close-btn-default);

        &:hover {
            color: var(--on-surface-hover, $close-btn-hover);
        }
    }

    &__progress {
        display: flex;
        gap: 6px;
        padding: 14px 20px 2px;

        &--has-close {
            padding-right: 44px;
        }
    }

    &__progress-seg {
        position: relative;
        flex: 1;
        height: 4px;
        border-radius: var(--border-radius-rounded, 15px);
        background: $dialog-progress-track;
        overflow: hidden;
    }

    &__progress-fill {
        position: absolute;
        inset: 0;
        width: 0;
        background: var(--primary, $brand-primary);
        border-radius: inherit;
        transition: width 0.34s cubic-bezier(0.25, 1, 0.5, 1);

        .dialog-sequence__progress-seg--done &,
        .dialog-sequence__progress-seg--active & {
            width: 100%;
        }

        .dialog-sequence__progress-seg--done & {
            background: var(--success, $brand-success);
        }
    }

    &__heading {
        padding: 18px 20px 0;

        &--has-close {
            padding-right: 44px;
        }

        &--start {
            text-align: left;
        }
    }

    &__overlay {
        position: absolute;
        inset: 0;
        z-index: 3;
        overflow: hidden;
        border-radius: inherit;
        // Empty most of the time; only what a step puts here takes clicks.
        pointer-events: none;

        > * {
            pointer-events: auto;
        }
    }

    &__kicker {
        margin-bottom: 8px;
        color: $dialog-text-faint;
        font-size: $font-size-xs;
        font-weight: $text-semi-bold;
        letter-spacing: 0.06em;
        text-transform: uppercase;
    }

    &__title {
        margin: 0;
        font-size: $dialog-title-font-size;
        font-weight: $text-bold;
        line-height: 1.25;
        text-wrap: balance;
    }

    &__lock-note {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 8px 0 0;
        color: $dialog-text-faint;
        font-size: $font-size-xs;

        &--footer {
            margin: 0 0 18px;
        }
    }

    &__rim {
        position: absolute;
        inset: 0;
        z-index: 1;
        width: 100%;
        height: 100%;
        overflow: visible;
        pointer-events: none;
    }

    &__rim-base,
    &__rim-live {
        fill: none;
    }

    &__rim-base {
        stroke: var(--primary, $brand-primary);
        stroke-width: 2;
        opacity: 0.2;
    }

    &__rim-live {
        stroke: $dialog-rim-live;
        stroke-width: 3.5;
        stroke-linecap: round;
        opacity: 0;
        filter: drop-shadow(0 0 4px var(--primary, $brand-primary))
            drop-shadow(0 0 11px color-mix(in srgb, var(--primary, #{$brand-primary}) 80%, transparent));
    }

    &__body {
        position: relative;
        // No lone word on a paragraph's last line, in every step's copy.
        text-wrap: pretty;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: 18px 20px 20px;

        &--after-heading {
            padding-top: 20px;
        }

        &--under-close {
            padding-top: 44px;
        }

        &--flush {
            display: grid;
            // Edge-to-edge content fills the body.
            grid-auto-rows: 1fr;
            padding: 0;
        }
    }

    &__footer {
        padding: 12px 20px 20px;
        box-shadow: 0 -8px 16px -12px rgba(20, 26, 40, 0.25);
    }

    &__loader {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    &__slot {
        display: contents;
    }

    &__aria-live {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
}

// Secondary text actions (back, maybe later, send again) stay quiet next to the primary.
.dialog-sequence__sheet :deep(.btn.btn--link) {
    color: $dialog-text-muted;

    &:hover,
    &:focus {
        color: var(--on-surface, $dialog-font-color);
    }
}
</style>

<style lang="scss">
// UIDialog teleports its .dialog (where this host's class lands) without this component's scope attribute,
// so these rules can't be scoped: they would never match.
.dialog.dialog-sequence {
    // Wider for long reading (terms), with the page still in view around it.
    &.dialog-sequence--wide {
        @include tablet {
            width: min(720px, calc(100vw - 96px));
            max-width: min(720px, calc(100vw - 96px));
        }
    }

    // A step that widens the sheet (terms over signup) grows it in place, and shrinks it again on the way back.
    @include tablet {
        @media (prefers-reduced-motion: no-preference) {
            transition:
                width 0.3s cubic-bezier(0.25, 1, 0.5, 1),
                max-width 0.3s cubic-bezier(0.25, 1, 0.5, 1);
        }
    }

    // The sheet lays out its own padding and scrolling.
    .dialog__body {
        padding: 0;
    }

    &.dialog--promotion {
        background: $dialog-sheet-bg;
    }
}
</style>
